import { useState } from 'react';
import { Eye, Paperclip } from 'lucide-react';
import { signedUrl, uploadDocument, type BucketId, type UploadedDocument } from '@/api/documents';
import { formatNumber } from '@/components/live';
import { DropZone } from '@/components/DropZone';
import { toast } from '@/lib/toast';

/**
 * Attach a file to a record.
 *
 * Buckets are private, so "view" asks for a signed URL that expires in a few minutes rather
 * than rendering a stored link. The link is opened and forgotten; nothing here keeps one.
 *
 * Camera capture is offered on a phone (`capture="environment"`), because the common case is a
 * field officer photographing a document in front of the farmer rather than picking a file.
 */
export function FileUpload({
  bucket,
  organizationId,
  ownerId,
  documentTypeId,
  label,
  accept,
  existing,
  onUploaded,
  disabled,
}: {
  bucket: BucketId;
  organizationId: string;
  ownerId: string;
  documentTypeId?: string | null;
  label: string;
  accept?: string;
  existing?: { storage_bucket: string; storage_path: string; original_filename?: string | null } | null;
  onUploaded?: (document: UploadedDocument) => void;
  disabled?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [uploaded, setUploaded] = useState<UploadedDocument | null>(null);

  const current = uploaded ?? existing ?? null;

  async function handle(file: File | undefined) {
    if (!file) return;
    if (!organizationId || !ownerId) {
      toast.error('Save the record first, then attach the file to it.');
      return;
    }
    setBusy(true);
    try {
      const document = await uploadDocument({
        file, bucket, organizationId, ownerId, documentTypeId,
        // A photograph taken now is `camera`; anything chosen from the device is `upload`.
        captureMethod: file.lastModified > Date.now() - 60_000 ? 'camera' : 'upload',
      });
      setUploaded(document);
      onUploaded?.(document);
      toast.success('Attached.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That file could not be attached.');
    } finally {
      setBusy(false);
    }
  }

  async function view() {
    if (!current) return;
    try {
      const url = await signedUrl(current.storage_bucket, current.storage_path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That file could not be opened.');
    }
  }

  return (
    <div className="file-upload">
      <span className="file-upload-label">{label}</span>

      {current ? (
        <div className="file-upload-current">
          <Paperclip size={14} />
          <span>{(current as UploadedDocument).original_filename ?? current.storage_path.split('/').pop()}</span>
          {'file_size_bytes' in current && (
            <em>{formatNumber(Math.round((current as UploadedDocument).file_size_bytes / 1024))} KB</em>
          )}
          <button onClick={() => void view()} type="button"><Eye size={14} /> View</button>
        </div>
      ) : (
        <p className="muted" style={{ margin: 0, fontSize: '12.5px' }}>Nothing attached yet.</p>
      )}

      <DropZone accept={accept ?? 'image/*,application/pdf'} busy={busy}
                buttonLabel={current ? 'Choose a replacement' : 'Choose file'}
                disabled={disabled} onFile={(file) => void handle(file)} />
      {current && (
        <small className="muted">
          Replacing attaches a new file; the previous one is kept as evidence.
        </small>
      )}
    </div>
  );
}
